let inited = false;

function isDebugEnabled() {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage?.getItem("ga4:debug") === "1") return true;
  } catch {}
  return String(process.env.REACT_APP_GA4_DEBUG || "").toLowerCase() === "true";
}

function safeString(v) {
  if (v == null) return "";
  return String(v);
}

function ensureDataLayer() {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  if (!window.gtag) {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
}

export function initGA4(measurementId) {
  if (typeof window === "undefined") return false;
  const id = safeString(measurementId || "").trim();
  if (!id) return false;
  if (inited) return true;

  ensureDataLayer();

  if (!document.getElementById("ga4-gtag")) {
    const s = document.createElement("script");
    s.id = "ga4-gtag";
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
    document.head.appendChild(s);
  }

  window.gtag("js", new Date());
  window.gtag("config", id, { send_page_view: false });

  if (isDebugEnabled()) {
    try {
      // eslint-disable-next-line no-console
      console.log("[GA4] init", { measurementId: id });
    } catch {}
  }

  inited = true;
  return true;
}

export function trackPageView({ measurementId, path, title }) {
  if (typeof window === "undefined") return;
  if (!initGA4(measurementId)) return;
  const id = safeString(measurementId || "").trim();
  if (!id) return;

  window.gtag("event", "page_view", {
    page_location: window.location.href,
    page_path: path,
    page_title: title || document.title,
  });
}

export function trackEvent({ measurementId, name, params }) {
  if (typeof window === "undefined") return;
  if (!initGA4(measurementId)) return;

  if (isDebugEnabled()) {
    try {
      // eslint-disable-next-line no-console
      console.log("[GA4] event", {
        name,
        params: params || {},
      });
    } catch {}
  }
  window.gtag("event", name, params || {});
}

export function trackViewItem({ measurementId, sku, name, price }) {
  trackEvent({
    measurementId,
    name: "view_item",
    params: {
      currency: "USD",
      value: Number(price) || 0,
      items: [
        {
          item_id: safeString(sku),
          item_name: safeString(name),
          price: Number(price) || 0,
          quantity: 1,
        },
      ],
    },
  });
}

export function trackAddToCart({ measurementId, sku, name, price, quantity }) {
  trackEvent({
    measurementId,
    name: "add_to_cart",
    params: {
      currency: "USD",
      value: (Number(price) || 0) * (Number(quantity) || 1),
      items: [
        {
          item_id: safeString(sku),
          item_name: safeString(name),
          price: Number(price) || 0,
          quantity: Number(quantity) || 1,
        },
      ],
    },
  });
}

export function trackRemoveFromCart({
  measurementId,
  sku,
  name,
  price,
  quantity,
}) {
  trackEvent({
    measurementId,
    name: "remove_from_cart",
    params: {
      currency: "USD",
      value: (Number(price) || 0) * (Number(quantity) || 1),
      items: [
        {
          item_id: safeString(sku),
          item_name: safeString(name),
          price: Number(price) || 0,
          quantity: Number(quantity) || 1,
        },
      ],
    },
  });
}

export function trackNewsletterSubscribe({ measurementId, source }) {
  trackEvent({
    measurementId,
    name: "sign_up",
    params: {
      method: safeString(source || "newsletter"),
    },
  });
}

export function trackBeginCheckout({ measurementId, value, coupon, items }) {
  trackEvent({
    measurementId,
    name: "begin_checkout",
    params: {
      currency: "USD",
      value: Number(value) || 0,
      coupon: coupon ? safeString(coupon) : undefined,
      items: Array.isArray(items) ? items : [],
    },
  });
}

export function trackPaymentMethodSelected({ measurementId, method, isGuest }) {
  trackEvent({
    measurementId,
    name: "payment_method_selected",
    params: {
      method: safeString(method),
      is_guest: !!isGuest,
    },
  });
}

export function trackCheckoutValidationError({
  measurementId,
  missingFields,
  methodAttempted,
  isGuest,
}) {
  const missing = Array.isArray(missingFields)
    ? missingFields.filter(Boolean).map((s) => safeString(s))
    : [];
  trackEvent({
    measurementId,
    name: "checkout_validation_error",
    params: {
      missing_fields: missing.join(","),
      method_attempted: safeString(methodAttempted),
      is_guest: !!isGuest,
    },
  });
}

export function trackPaymentError({
  measurementId,
  provider,
  stage,
  message,
  methodAttempted,
}) {
  trackEvent({
    measurementId,
    name: "payment_error",
    params: {
      provider: safeString(provider),
      stage: safeString(stage),
      method_attempted: safeString(methodAttempted),
      message: safeString(message).slice(0, 200),
    },
  });
}

export function trackShippingInsuranceSelected({
  measurementId,
  insuranceTotal,
  cartValue,
  itemsCount,
}) {
  trackEvent({
    measurementId,
    name: "shipping_insurance_selected",
    params: {
      insurance_total: Number(insuranceTotal) || 0,
      cart_value: Number(cartValue) || 0,
      items_count: Number(itemsCount) || 0,
    },
  });
}

export function trackPurchase({
  measurementId,
  transactionId,
  value,
  tax,
  shipping,
  coupon,
  items,
}) {
  trackEvent({
    measurementId,
    name: "purchase",
    params: {
      transaction_id: safeString(transactionId),
      currency: "USD",
      value: Number(value) || 0,
      tax: Number(tax) || 0,
      shipping: Number(shipping) || 0,
      coupon: coupon ? safeString(coupon) : undefined,
      items: Array.isArray(items) ? items : [],
    },
  });
}
